import React, { useState, useEffect } from "react";
import "../../Styles/AddClassesSubjects.css";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";

function EditclassesSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [organizationName, setOrganizationName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setInitialLoading(false);
          return;
        }

        const response = await axios.get(
          "https://api.uniums.com/api/admin/get-class-subjects",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          setIsDataAvailable(true);
          setSubjects(response.data.subjects || []);
          setClasses(response.data.classes || []);
          setSemesters(response.data.semesters || []);
          setOrganizationName(response.data.organizationName || "");
        } else {
          setIsDataAvailable(false);
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        setIsDataAvailable(false);
        toast.error("Failed to fetch data");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchFormData();
  }, []);

  const handleAddField = (setter, currentValues) => {
    const lastValue = currentValues[currentValues.length - 1];
    if (lastValue.trim() !== '') {
      setter([...currentValues, ""]);
    } else {
      toast.error("Please fill the existing field before adding more.");
    }
  };

  const handleRemoveField = (index, setter, currentValues) => {
    if (currentValues.length > 1) {
      const updatedValues = currentValues.filter((_, i) => i !== index);
      setter(updatedValues);
    } else {
      toast.error("At least one field is required.");
    }
  };

  const handleInputChange = (index, value, setter, currentValues) => {
    const updatedValues = [...currentValues];
    updatedValues[index] = value;
    setter(updatedValues);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!token) {
      toast.error("You need to be logged in to submit.");
      return;
    }

    const validateFields = (fields) => {
      return fields.filter(field => field.trim() !== '');
    };

    const filteredSubjects = validateFields(subjects);
    const filteredClasses = validateFields(classes);
    const filteredSemesters = validateFields(semesters);

    if (filteredSubjects.length === 0 || filteredClasses.length === 0 || filteredSemesters.length === 0) {
      toast.error("Please provide at least one non-empty value for Subjects, Classes, and Semesters.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        "https://api.uniums.com/api/admin/save-class-subjects",
        {
          organizationName,
          subjects: filteredSubjects,
          classes: filteredClasses,
          semesters: filteredSemesters,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(response.data.message);
      setIsDataAvailable(true);
    } catch (error) {
      toast.error("An error occurred while saving the data.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem("authToken");
    const validateFields = (fields) => {
      return fields.filter(field => field.trim() !== '');
    };

    const filteredSubjects = validateFields(subjects);
    const filteredClasses = validateFields(classes);
    const filteredSemesters = validateFields(semesters);

    if (filteredSubjects.length === 0 || filteredClasses.length === 0 || filteredSemesters.length === 0) {
      toast.error("Please provide at least one non-empty value for Subjects, Classes, and Semesters.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.put(
        "https://api.uniums.com/api/admin/edit-class-subjects",
        {
          organizationName,
          subjects: filteredSubjects,
          classes: filteredClasses,
          semesters: filteredSemesters,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(response.data.message);
      setIsEditMode(false);
    } catch (error) {
      toast.error("An error occurred while updating the data.");
    } finally {
      setLoading(false);
    }
  };

  const renderInputs = (values, setter, label) => (
    <div className="form-group">
      <label>{label}:</label>
      <div className="dynamic-fields">
        {values.map((value, index) => (
          <React.Fragment key={`${label}-${index}`}>
            <div className="input-wrapper">
              <input
                type="text"
                value={value}
                onChange={(e) =>
                  handleInputChange(index, e.target.value, setter, values)
                }
                placeholder={`${label} ${index + 1}`}
                disabled={!isEditMode}
              />
              {isEditMode && (
                <div className="field-actions">
                  {index === values.length - 1 && (
                    <button
                      type="button"
                      className="add-more-btn"
                      onClick={() => handleAddField(setter, values)}
                    >
                      Add More
                    </button>
                  )}
                  {values.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveField(index, setter, values)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // If initial loading, show the loader
  if (initialLoading) {
    return <Loader />;
  }

  return (
    <div className="AddClassesSubjects">
      {loading && <Loader />}
      
      {isDataAvailable ? (
        <div>
          <h1>Your Submitted Data</h1>
          <div className="form-container">
            <div className="form-group">
              <label>Organization Name:</label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                disabled={!isEditMode}
              />
            </div>
            {renderInputs(subjects, setSubjects, "Subjects")}
            {renderInputs(classes, setClasses, "Classes")}
            {renderInputs(semesters, setSemesters, "Semesters")}
            {isEditMode ? (
              <button
                className="submit-btn"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            ) : (
              <button className="submit-btn" onClick={handleEdit}>
                Edit
              </button>
            )}
          </div>
        </div>
      ) : (
        <div>
          <h1>Add Classes, Subjects, and Semesters</h1>
          <div className="form-container">
            <div className="form-group">
              <label>Organization Name:</label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Enter Organization Name"
                disabled
              />
            </div>
            {renderInputs(subjects, setSubjects, "Subjects")}
            {renderInputs(classes, setClasses, "Classes")}
            {renderInputs(semesters, setSemesters, "Semesters")}
            <button
              className="submit-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default EditclassesSubjects;